// A database error, said to a person who did not cause it and cannot fix it.
//
// ── THE DEFECT THIS EXISTS FOR ────────────────────────────────────────────
// Thirty-seven places in HQ answered a failed save with `alert(error.message)`.
// What that puts in front of the owner of the company is text like:
//
//   null value in column "win" of relation "hq_wins" violates not-null constraint
//   new row violates row-level security policy for table "hq_bugs"
//   column hq_employees.is_admin does not exist
//
// Table names, column names, constraint names and the word "relation". None of
// it tells him what happened, none of it tells him what to do, and all of it
// reads as though HQ is broken in a way he is expected to understand.
//
// ── WHAT A GOOD MESSAGE ANSWERS ───────────────────────────────────────────
// What failed, whether anything changed, and what to do next. Where the code
// says which of those is true, this says it. Where it does not, this says that
// honestly rather than inventing a reassurance — "something went wrong" that
// implies nothing was saved, when something was, is worse than the raw error.
//
// The original text is not discarded. It goes to the console, where an engineer
// diagnosing a fault will look and where it belongs.
//
// Pure, apart from the console line.

export interface DbError {
  message?: string;
  code?: string;
  details?: string;
  hint?: string;
}

/**
 * Postgres error codes HQ actually produces, and what each one means to the
 * person who just pressed a button.
 */
const BY_CODE: Record<string, string> = {
  // Unique violation.
  "23505": "That already exists. Nothing was changed.",
  // Foreign key violation.
  "23503": "Something this depends on is missing or is still in use. Nothing was changed.",
  // Not-null violation.
  "23502": "Something required was left empty. Nothing was saved.",
  // Check constraint.
  "23514": "That combination is not allowed. Nothing was saved.",
  // Undefined column / undefined table — a fault in HQ, not in what was typed.
  "42703": "HQ has a fault here and this cannot be saved until it is fixed. Nothing was changed.",
  "42P01": "HQ has a fault here and this cannot be saved until it is fixed. Nothing was changed.",
  // Insufficient privilege / RLS.
  "42501": "Your account is not allowed to do that. Nothing was changed.",
  // Invalid text representation, e.g. a malformed id.
  "22P02": "One of those values is not in a form HQ can read. Nothing was saved.",
  // PostgREST: no rows returned where one was expected.
  PGRST116: "That could not be found. It may have been removed by somebody else.",
};

/** Patterns for errors that arrive without a usable code. */
const BY_TEXT: Array<[RegExp, string]> = [
  [/row-level security/i, "Your account is not allowed to do that. Nothing was changed."],
  [/duplicate key/i, "That already exists. Nothing was changed."],
  [/violates not-null/i, "Something required was left empty. Nothing was saved."],
  [/violates check constraint/i, "That combination is not allowed. Nothing was saved."],
  [/does not exist/i, "HQ has a fault here and this cannot be saved until it is fixed. Nothing was changed."],
  [/fetch|network|failed to fetch/i, "HQ could not be reached. Check your connection and try again."],
  [/jwt|token|expired/i, "Your session has expired. Reload the page and sign in again."],
];

/**
 * The sentence to show. `action` names what was being attempted, in the words
 * of the screen — "save that win", "delete the role" — so the message says what
 * failed rather than only that something did.
 */
export function plainError(e: unknown, action?: string): string {
  const err = (e ?? {}) as DbError;
  const raw = typeof err.message === "string" ? err.message : String(e ?? "");

  // Kept for whoever is diagnosing, out of the way of whoever is working.
  if (raw) console.error("[hq]", action ? `${action}:` : "", raw, err.code ?? "");

  const byCode = err.code ? BY_CODE[err.code] : undefined;
  const byText = byCode ? undefined : BY_TEXT.find(([re]) => re.test(raw))?.[1];
  const said = byCode ?? byText;

  const prefix = action ? `Could not ${action}.` : "That did not work.";
  // No match: say what is actually known, which is that it failed and that the
  // detail is not something HQ can explain. Never guess that nothing changed.
  return said ? `${prefix} ${said}` : `${prefix} HQ did not say why.`;
}
